// app.component.ts
import { Component, OnInit, inject, signal, effect, AfterViewInit, DestroyRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { Router, NavigationStart, NavigationEnd, NavigationError } from '@angular/router';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationService } from './shared/services/navigation.service';
import { NotificationService } from './shared/services/notification.service';
import { NotificationType } from './models/notification.model';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less']
})
export class AppComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private notificationService = inject(NotificationService);
  private destroyRef = inject(DestroyRef);
  private ngZone = inject(NgZone);

  private currentRouterEvent = toSignal(
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)),
    { initialValue: null }
  );

  loading = signal(false);

  constructor() {
    effect(() => {
      const event = this.currentRouterEvent();
      
      if (event instanceof NavigationStart) {
        this.handleNavigationStart();
      } else if (event instanceof NavigationEnd) {
        this.handleNavigationEnd(event);
      } else if (event instanceof NavigationError) {
        this.handleNavigationError(event);
      }
    });

  }

  ngOnInit(): void {
    console.log('[AppComponent] ngOnInit');
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.initializeMauiIntegration(), 500);
  }

  private handleNavigationStart(): void {
    this.loading.set(true);
  }

  private handleNavigationEnd(event: NavigationEnd): void {
    this.loading.set(false);
    console.log('[AppComponent] Navigation ended:', event.url);
    
    setTimeout(() => {
      this.navigationService.updateMauiNavigationState();
      this.logCurrentNavigationState();
    }, 100);
  }

  private handleNavigationError(event: NavigationError): void {
    this.loading.set(false);
    console.error('[AppComponent] Navigation error:', event.error);
  }

  private initializeMauiIntegration(): void {
    console.log('[AppComponent] Signaling app ready to MAUI...');

    this.ensureMauiBridgeExists();
    this.notifyMauiAppReady();
    this.logCurrentNavigationState();
    this.registerPushBridge();

    console.log('[AppComponent] Signaled app ready to MAUI');
  }

  private ensureMauiBridgeExists(): void {
    const windowWithBridge = window as any;
    
    if (!windowWithBridge.angularNavigation) {
      console.warn('[AppComponent] Angular navigation bridge not found, reinitializing...');
      this.navigationService.initializeMauiBridge();
    }
  }

  private notifyMauiAppReady(): void {
    this.sendAppReadySignal();
    this.callMauiReadyCallback();
  }

  private sendAppReadySignal(): void {
    window.location.href = 'nginspector://app-ready';
    console.log('[AppComponent] Sent app-ready signal via location.href');
  }

  private callMauiReadyCallback(): void {
    const windowWithMaui = window as any;

    if (windowWithMaui.mauiAppReady) {
      windowWithMaui.mauiAppReady();
      console.log('[AppComponent] Called mauiAppReady callback');
    }
  }

  /**
   * Registers permanent MAUI JS bridges so they survive Angular route changes.
   * - window.refreshMainPage                  – legacy: called by MAUI to reload the dashboard
   * - window.onPushNotificationReceived(type, id) – called by MAUI when a push arrives while the app is open
   * - window.onPushNotificationTapped(type, id)   – called by MAUI when the user taps a notification
   */
  private registerPushBridge(): void {
    const w = window as any;

    w.refreshMainPage = (type?: number, notificationId?: string) => {
      console.log('[AppComponent] refreshMainPage called from MAUI', type, notificationId);
      const notifType = (type !== undefined && type !== null) ? type as NotificationType : NotificationType.Transaction;
      this.ngZone.run(() => this.notificationService.notifyPushReceived(notifType, notificationId ?? ''));
    };

    w.onPushNotificationReceived = (type?: number, notificationId?: string) => {
      console.log('[AppComponent] onPushNotificationReceived called from MAUI', type, notificationId);
      const notifType = (type !== undefined && type !== null) ? type as NotificationType : NotificationType.Transaction;
      this.ngZone.run(() => this.notificationService.notifyPushReceived(notifType, notificationId ?? ''));
    };

    w.onPushNotificationTapped = (type?: number, notificationId?: string) => {
      console.log('[AppComponent] onPushNotificationTapped called from MAUI', type, notificationId);
      const notifType = (type !== undefined && type !== null) ? type as NotificationType : NotificationType.Transaction;
      this.ngZone.run(() => this.notificationService.notifyPushTapped(notifType, notificationId ?? ''));
    };

    console.log('[AppComponent] MAUI push bridges registered');
  }

  private logCurrentNavigationState(): void {
    const navigationState = {
      canNavigateUp: this.navigationService.canNavigateUp(),
      currentRoute: this.router.url,
      currentPage: this.navigationService.getCurrentPageInfo(),
      backButtonLabel: this.navigationService.getTranslatedBackText(),
    };
    
    console.log('[AppComponent] Current navigation state:', navigationState);
  }
}
